import React from "react";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Modal, Space, Spin, Table, Tag, Upload, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  deleteComplianceDocument,
  fetchComplianceDocumentBlob,
  attachSamplePack,
  uploadComplianceDocument,
} from "../../api";
import type { ComplianceCaseDetail, ComplianceDocument, DocumentCategory } from "../types";
import {
  DOCUMENT_CATEGORY_VALUES,
  EXTRACTION_FIELD_KEYS,
  documentsMinReady,
} from "../complianceLabels";
import { PolygonSchematic } from "./PolygonSchematic";
import { PlotMapEditor } from "./PlotMapEditor";
import { MobileDataCards } from "./MobileDataCards";
import { useNarrowViewport } from "../ux/useNarrowViewport";

type Props = {
  caseData: ComplianceCaseDetail;
  onUpdated: () => void;
};

type PreviewKind = "pdf" | "image" | "none";

function previewKind(contentType: string | null | undefined): PreviewKind {
  if (!contentType) return "none";
  if (contentType === "application/pdf" || contentType.includes("pdf")) return "pdf";
  if (contentType.startsWith("image/")) return "image";
  return "none";
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function DocumentUploadStep({ caseData, onUpdated }: Props) {
  const { t } = useTranslation();
  const isNarrow = useNarrowViewport("md");
  const [uploading, setUploading] = React.useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = React.useState<ComplianceDocument | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewKindState, setPreviewKindState] = React.useState<PreviewKind>("none");
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);

  const categoryLabel = (cat: DocumentCategory) =>
    t(`compliance.documents.categories.${cat}`, {
      defaultValue: t(`compliance.documentCategories.${cat}`, { defaultValue: cat }),
    });

  const fieldLabel = (key: string) =>
    t(`compliance.extraction.fields.${key}`, { defaultValue: key.replace(/_/g, " ") });

  const revokePreviewUrl = React.useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const closePreview = React.useCallback(() => {
    revokePreviewUrl();
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewKindState("none");
    setPreviewLoading(false);
  }, [revokePreviewUrl]);

  React.useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

  const openPreview = async (doc: ComplianceDocument) => {
    revokePreviewUrl();
    setPreviewUrl(null);
    setPreviewKindState("none");
    setPreviewDoc(doc);
    setPreviewLoading(true);
    try {
      const { blob, contentType } = await fetchComplianceDocumentBlob(caseData.id, doc.id);
      const kind = previewKind(contentType || doc.content_type);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewKindState(kind);
    } catch {
      message.error(t("compliance.documents.previewFailed"));
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUpload = async (category: DocumentCategory, file: File) => {
    setUploading(category);
    try {
      await uploadComplianceDocument(caseData.id, category, file);
      message.success(t("compliance.documents.uploaded"));
      onUpdated();
    } catch (err) {
      message.error(t("compliance.documents.uploadFailed"));
      throw err;
    } finally {
      setUploading(null);
    }
  };

  const categoriesPresent = new Set(caseData.documents.map((d) => d.category));
  const minReady = documentsMinReady(caseData.documents);

  const extractedTags = (doc: ComplianceDocument) => {
    const fields = doc.extracted_fields;
    if (!fields) return null;
    const entries = Object.entries(fields).filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (entries.length === 0) return null;
    return (
      <Space size={[4, 4]} wrap style={{ marginTop: 4 }}>
        {entries.slice(0, 4).map(([key, val]) => (
          <Tag key={key} title={formatFieldValue(val)}>
            {fieldLabel(key)}: {formatFieldValue(val).slice(0, 40)}
            {formatFieldValue(val).length > 40 ? "…" : ""}
          </Tag>
        ))}
        {entries.length > 4 && (
          <Tag>{t("compliance.documents.moreFields", { count: entries.length - 4 })}</Tag>
        )}
      </Space>
    );
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Button
        onClick={async () => {
          try {
            await attachSamplePack(caseData.id);
            message.success(t("compliance.documents.samplePackLoaded"));
            onUpdated();
          } catch {
            message.error(t("compliance.documents.samplePackFailed"));
          }
        }}
      >
        {t("compliance.documents.loadSamplePack")}
      </Button>
      {DOCUMENT_CATEGORY_VALUES.map((cat) => (
        <Card key={cat.value} size="small" title={categoryLabel(cat.value)}>
          <Upload
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              handleUpload(cat.value, file as unknown as File).catch(() => undefined);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploading === cat.value}>
              {t("compliance.documents.uploadButton", { category: categoryLabel(cat.value) })}
            </Button>
          </Upload>
          {cat.required && !categoriesPresent.has(cat.value) && (
            <span style={{ marginLeft: 12, color: "#cf1322" }}>{t("common.required")}</span>
          )}
          {cat.value === "geolocation" && (
            <div style={{ marginTop: 16 }}>
              <PlotMapEditor
                saving={uploading === "geolocation"}
                onSave={async (file) => {
                  await handleUpload("geolocation", file);
                }}
              />
            </div>
          )}
        </Card>
      ))}

      {isNarrow ? (
        <MobileDataCards
          testId="documents-mobile-cards"
          items={caseData.documents}
          rowKey={(row) => row.id}
          title={(row) => row.original_filename}
          fields={(row) => [
            { label: t("common.category"), value: categoryLabel(row.category as DocumentCategory) },
            { label: t("common.size"), value: `${(row.size_bytes / 1024).toFixed(1)} KB` },
            { label: t("common.by"), value: row.uploaded_by },
          ]}
          description={(row) => extractedTags(row)}
          actions={(row) => (
            <Space wrap size="small">
              <Button
                type="link"
                className="dds-touch-target"
                onClick={() => openPreview(row)}
              >
                {t("common.open")}
              </Button>
              <Button
                type="text"
                danger
                className="dds-touch-target"
                icon={<DeleteOutlined />}
                aria-label="Delete"
                onClick={async () => {
                  await deleteComplianceDocument(caseData.id, row.id);
                  onUpdated();
                }}
              />
            </Space>
          )}
        />
      ) : (
      <Table
        rowKey="id"
        size="small"
        dataSource={caseData.documents}
        pagination={false}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          rowExpandable: (row) =>
            Boolean(row.extracted_fields && Object.keys(row.extracted_fields).length > 0),
          expandedRowRender: (row) => {
            const cat = row.category as DocumentCategory;
            const keys = EXTRACTION_FIELD_KEYS[cat] ?? Object.keys(row.extracted_fields ?? {});
            const fields = row.extracted_fields ?? {};
            const skipInList = new Set([
              "preview_rings",
              "geometry_issues",
              "geometry_valid",
              "crs",
              "crs_assumed",
              "area_ha",
              "polygon_count",
              "geometry_type",
              "source_format",
            ]);
            return (
              <Space direction="vertical" size="small" style={{ paddingLeft: 8, width: "100%" }}>
                {cat === "geolocation" && fields.preview_rings != null && (
                  <PolygonSchematic fields={fields} />
                )}
                {keys.map((key) => {
                  if (cat === "geolocation" && skipInList.has(key)) return null;
                  const val = fields[key];
                  if (val === null || val === undefined || val === "") return null;
                  return (
                    <div key={key}>
                      <strong>{fieldLabel(key)}:</strong> {formatFieldValue(val)}
                    </div>
                  );
                })}
              </Space>
            );
          },
        }}
        columns={[
          {
            title: t("common.category"),
            dataIndex: "category",
            render: (cat: DocumentCategory) => categoryLabel(cat),
          },
          {
            title: t("common.file"),
            dataIndex: "original_filename",
            render: (name: string, row: ComplianceDocument) => (
              <div>
                <Button type="link" style={{ padding: 0 }} onClick={() => openPreview(row)}>
                  {name}
                </Button>
                {extractedTags(row)}
              </div>
            ),
          },
          {
            title: t("common.size"),
            dataIndex: "size_bytes",
            render: (n: number) => `${(n / 1024).toFixed(1)} KB`,
          },
          { title: t("common.by"), dataIndex: "uploaded_by" },
          {
            title: "",
            render: (_, row) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={async () => {
                  await deleteComplianceDocument(caseData.id, row.id);
                  onUpdated();
                }}
              />
            ),
          },
        ]}
      />
      )}

      <Modal
        title={t("compliance.documents.previewTitle")}
        open={previewDoc !== null}
        onCancel={closePreview}
        footer={<Button onClick={closePreview}>{t("compliance.documents.close")}</Button>}
        width={previewKindState === "none" ? 480 : 900}
        destroyOnClose
      >
        {previewLoading && (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin />
          </div>
        )}
        {!previewLoading && previewUrl && previewKindState === "pdf" && (
          <iframe
            src={previewUrl}
            title={previewDoc?.original_filename}
            style={{ width: "100%", height: "70vh", border: "none" }}
          />
        )}
        {!previewLoading && previewUrl && previewKindState === "image" && (
          <img
            src={previewUrl}
            alt={previewDoc?.original_filename}
            style={{ maxWidth: "100%", maxHeight: "70vh", display: "block", margin: "0 auto" }}
          />
        )}
        {!previewLoading && previewUrl && previewKindState === "none" && (
          <Space direction="vertical">
            <p>{t("compliance.documents.previewUnavailable")}</p>
            <Button type="link" href={previewUrl} download={previewDoc?.original_filename}>
              {t("common.download")}: {previewDoc?.original_filename}
            </Button>
          </Space>
        )}
      </Modal>

      {!minReady && (
        <Alert
          type="warning"
          message={t("compliance.documents.minReadyWarning")}
          style={{ marginBottom: 20 }}
        />
      )}
    </Space>
  );
}
